import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { db } from "../firebase-config";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Modal from "@mui/material/Modal";
import AddForm from "./AddForm";
import EditForm from "./EditForm";
import Skeleton from "@mui/material/Skeleton";
import { useAppStore } from "../appStore";

// ... imports remain the same

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 500,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

export default function ProductsList() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rows = useAppStore((state) => state.rows);
  const setRows = useAppStore((state) => state.setRows);
  const empCollectionRef = collection(db, "products");

  const [formid, setFormid] = useState("");
  const [open, setOpen] = useState(false);
  const [editopen, setEditOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleEditOpen = () => setEditOpen(true);
  const handleClose = () => setOpen(false);
  const handleEditClose = () => setEditOpen(false);

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = async () => {
    const data = await getDocs(empCollectionRef);
    setRows(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const deleteUser = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) deleteApi(id);
    });
  };

  const deleteApi = async (id) => {
    const userDoc = doc(db, "products", id);
    // await deleteDoc(userDoc);
    Swal.fire("Deleted!", "Your file has been deleted.", "success");
    getUsers();
  };

  const filterData = (v) => (v ? setRows([v]) : getUsers());

  const editData = (id, name, price, category) => {
    setFormid({ id, name, price, category });
    handleEditOpen();
  };

  return (
    <>
      {/* Modals */}
      <Modal open={open} aria-labelledby="modal-modal-title">
        <Box sx={style}>
          <AddForm closeEvent={handleClose} />
        </Box>
      </Modal>
      <Modal open={editopen} aria-labelledby="modal-modal-title">
        <Box sx={style}>
          <EditForm closeEvent={handleEditClose} fid={formid} />
        </Box>
      </Modal>

      {/* Product Table */}
      {rows.length > 0 ? (
        <Paper sx={{ width: "100%", overflowX: "auto", p: 2 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Products List
          </Typography>
          <Divider />
          <Box sx={{ my: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={rows}
                sx={{ width: { xs: "100%", sm: 300 } }}
                onChange={(e, v) => filterData(v)}
                getOptionLabel={(row) => row.name || ""}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Search Products" />
                )}
              />
              <Button
                variant="contained"
                endIcon={<AddCircleIcon />}
                onClick={handleOpen}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Add
              </Button>
            </Stack>
          </Box>

          <TableContainer sx={{ overflowX: "auto" }}>
            <Table stickyHeader aria-label="product table">
              <TableHead>
                <TableRow>
                  {["Name", "Price", "Category", "Date", "Action"].map((head) => (
                    <TableCell key={head}>{head}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{String(row.price)}</TableCell>
                      <TableCell>{row.category}</TableCell>
                      {/* <TableCell>{String(row.date)}</TableCell> */}
                      <TableCell>
                        {row.date && new Date(row.date.seconds ? row.date.seconds * 1000 : row.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <EditIcon
                            sx={{ color: "primary.main", cursor: "pointer" }}
                            onClick={() =>
                              editData(row.id, row.name, row.price, row.category)
                            }
                          />
                          <DeleteIcon
                            sx={{ color: "error.main", cursor: "pointer" }}
                            onClick={() => deleteUser(row.id)}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : (
        <Paper sx={{ width: "100%", p: 2 }}>
          <h2>Please set up firebase-config.js file first</h2>
          {[...Array(5)].map((_, idx) => (
            <Box key={idx} sx={{ mb: 2 }}>
              <Skeleton variant="rectangular" width="100%" height={60} />
            </Box>
          ))}
        </Paper>
      )}
    </>
  );
}
